import React, { useState } from "react";
import { ChevronDown, Rocket, CreditCard, Coins, Settings, Video, Code, Phone, Shield, Lightbulb, Wrench, User, Briefcase, HelpCircle } from "lucide-react";
import SEO from "../components/SEO";

const CATEGORIES = [
  {
    id: "getting-started", label: "Getting Started", icon: Rocket, count: 4,
    sections: [
      {
        title: "Overview",
        questions: [
          {
            q: "What is Wingman AI and how does it work?",
            a: "Wingman AI is a real-time interview assistant that listens to your interview, understands the question being asked, and instantly surfaces the best possible answer on your screen — all while remaining invisible to your interviewer. You set up your role, company, and interview type before the session. During the interview, Wingman AI transcribes what the interviewer says, processes it through advanced AI models, and gives you structured, role-specific answers you can speak naturally."
          },
          {
            q: "What is the Wingman AI Dashboard?",
            a: "The Wingman AI Dashboard is your command center. From here you can start a new interview session (Live, Mock, or Coding), choose your AI model, enter your company and position details, and review your past sessions. It also gives you access to the Resource Hub, Session History, and your account settings."
          },
        ]
      },
      {
        title: "First Steps",
        questions: [
          {
            q: "How do I set up Wingman AI for my first interview?",
            a: "1. Register or log in at projectwingman.thesecretsociety.in. 2. From the Dashboard, select your interview mode (Live, Mock, or Coding). 3. Enter the company name, position, scenario, and any additional context in the Interview Setup form. 4. Click Start. The session opens instantly — enable your mic to start live transcription or type questions manually."
          },
          {
            q: "Can I try Wingman AI without buying a paid plan?",
            a: "Yes. The Free plan lets you run up to 3 mock interviews per month with basic AI coaching and 7-day session history — no credit card required. Simply register and start immediately. Upgrade to Pro or Premium whenever you're ready for unlimited sessions."
          },
        ]
      }
    ]
  },
  {
    id: "plans-pricing", label: "Plans & Pricing", icon: CreditCard, count: 6,
    sections: [
      {
        title: "Plans",
        questions: [
          { q: "What plans does Wingman AI offer?", a: "Wingman AI has three plans: Free (₹0) — 3 mock interviews/month, basic AI coaching, 7-day history. Pro (₹1 one-time) — unlimited live and mock interviews, GPT-4o with web search, real-time transcription, 30-day history, coding mode. Premium (₹5 one-time) — everything in Pro plus priority AI models, unlimited history, resume and cover letter coach, Resource Hub Pro, and email support." },
          { q: "Is Wingman AI a subscription or one-time payment?", a: "Both Pro and Premium are currently offered as one-time payments during our early access period, not monthly subscriptions. You pay once and keep access." },
          { q: "How do I upgrade my plan?", a: "Go to the Pricing page from the sidebar or header. Click 'Upgrade to Pro' or 'Upgrade to Premium'. The Razorpay payment window opens — complete the payment via UPI, card, or netbanking. Your plan upgrades instantly once payment is verified." },
        ]
      },
      {
        title: "Payments",
        questions: [
          { q: "What payment methods are accepted?", a: "We accept UPI (Google Pay, PhonePe, Paytm, BHIM, any UPI app), debit/credit cards, and netbanking — all powered by Razorpay." },
          { q: "Is my payment secure?", a: "Yes. All payments are processed by Razorpay, which is PCI-DSS compliant. Wingman AI never sees or stores your card or UPI details." },
          { q: "Can I get a refund?", a: "Since plans are priced at ₹1 and ₹5 for early access, refunds are evaluated case-by-case. Contact support with your registered email and payment ID if you have an issue." },
        ]
      }
    ]
  },
  {
    id: "credits-billing", label: "Credits & Billing", icon: Coins, count: 5,
    sections: [
      {
        title: "Billing",
        questions: [
          { q: "How does billing work on the Free plan?", a: "The Free plan has no billing. You get 3 mock sessions per month at no cost. Usage resets on the 1st of each calendar month." },
          { q: "Will I be charged automatically after upgrading?", a: "No. Both Pro and Premium are one-time payments. There is no recurring charge or auto-renewal." },
          { q: "How do I check my current plan?", a: "Your current plan is shown in the sidebar next to your name. You can also see it on the Pricing page — your active plan will show 'Current plan' instead of an upgrade button." },
          { q: "What happens if my payment fails?", a: "If payment fails mid-checkout, your plan is not changed. The Razorpay window will show the error. Simply retry — your previous plan remains active." },
          { q: "How do I contact billing support?", a: "Email us through the Support section in the Resource Hub. Include your registered email address and the transaction ID from Razorpay for faster resolution." },
        ]
      }
    ]
  },
  {
    id: "setup", label: "Setup & Configuration", icon: Settings, count: 6,
    sections: [
      {
        title: "Browser & Microphone",
        questions: [
          { q: "Which browsers does Wingman AI support?", a: "Wingman AI works best on Google Chrome. Firefox and Edge are supported with minor limitations. Safari has limited Web Speech API support so live transcription may not work on Safari — use Chrome for the best experience." },
          { q: "How do I enable microphone access?", a: "When you click 'Listen' for the first time, your browser will ask for microphone permission. Click 'Allow'. If you accidentally blocked it, go to your browser's site settings for projectwingman.thesecretsociety.in and set Microphone to 'Allow', then refresh." },
          { q: "Do I need to install anything?", a: "No installation required. Wingman AI runs entirely in your browser. Just open the site, log in, and start your session." },
        ]
      },
      {
        title: "Interview Setup",
        questions: [
          { q: "What should I enter in the Interview Setup form?", a: "Enter the company you're interviewing at (e.g. Infosys, ServiceNow, Google), the exact position title (e.g. Pre-Sales Architect, Senior Backend Engineer), the scenario type, and any additional objective or context. The more specific you are, the more role-accurate the AI answers will be." },
          { q: "Can I change the AI model for a session?", a: "The model picker is a display-only showcase. You can select ChatGPT and Claude models to change the label shown in the picker, but the application's configured production model continues to power every session." },
          { q: "What is the Objective field for?", a: "Use the Objective field to give the AI extra context — for example: 'This is a technical round with the CTO', 'Behavioral interview focused on leadership', or 'Pre-sales demo discussion'. This helps the AI calibrate tone, depth, and what the interviewer is likely evaluating." },
        ]
      }
    ]
  },
  {
    id: "live-mode", label: "Live Interview Mode", icon: Video, count: 6,
    sections: [
      {
        title: "How It Works",
        questions: [
          { q: "How does Live Mode work?", a: "In Live Mode, Wingman AI listens to the interviewer's questions through your microphone using Web Speech Recognition. It transcribes the question, sends it to the AI, and streams back the best answer in real-time. The best answer appears in the left panel; all three answer options appear in the right panel for you to choose from." },
          { q: "What are the three answer options?", a: "For every question in Live Mode, Wingman AI generates three answers: Best Answer (the strongest, most complete response — displayed prominently on the left), Option 2 (a solid alternative with a different angle or tone), and Option 3 (a third viable approach — more concise, more technical, or more story-driven depending on the question). You speak whichever feels most natural." },
          { q: "Can I also type questions manually?", a: "Yes. The input box at the bottom of the left panel lets you type any question and press Enter or click Send. This is useful if the mic doesn't pick up a question clearly, or if you want to pre-prepare for expected questions." },
        ]
      },
      {
        title: "Audio & Transcription",
        questions: [
          { q: "What if the microphone misses a question?", a: "Simply type the question manually in the input box and hit Enter — you'll get the same AI-generated answers instantly. You can also click Stop and then Listen again to reset the recognition session." },
          { q: "Can Wingman AI hear both sides of the call?", a: "Wingman AI's microphone listens to whatever audio your device's mic picks up. If the interviewer's voice comes through your speakers loudly enough, it will be transcribed. For best results, use headphones so the interviewer's voice doesn't bleed into your mic — instead, type their questions manually or use screen share audio routing." },
          { q: "Is there a delay in getting answers?", a: "Answers stream in within 1–3 seconds of the question being submitted, depending on your internet connection and the AI model selected. The response streams word-by-word so you start reading immediately." },
        ]
      }
    ]
  },
  {
    id: "coding", label: "Coding & Assessments", icon: Code, count: 5,
    sections: [
      {
        title: "Coding Mode",
        questions: [
          { q: "How does Coding Mode work?", a: "Select 'Coding' as your interview mode on the Dashboard, fill in the company and position, then start the session. Type or paste the coding problem into the input box. Wingman AI returns an optimal solution in the right language for your role, with a step-by-step explanation, time/space complexity analysis, and trade-off discussion." },
          { q: "Which programming languages does Wingman AI support?", a: "Wingman AI automatically picks the most appropriate language based on your role — Python for data science/ML, JavaScript/TypeScript for frontend, Java or Go for backend systems, SQL for data analytics, and so on. You can also specify a language in the question if needed." },
          { q: "Can Wingman AI help with system design questions?", a: "Yes. For system design questions, Wingman AI covers scale assumptions, component architecture, data models, API contracts, caching strategies, failure modes, and trade-offs — all calibrated to the company and role you specified in the setup." },
          { q: "Can I use Coding Mode for LeetCode-style problems?", a: "Absolutely. Paste the problem statement directly into the input box. Wingman AI will explain the optimal approach, write clean code, analyse time and space complexity, and highlight edge cases — exactly what interviewers look for." },
          { q: "Is Coding Mode available on the Free plan?", a: "Coding Mode is available on the Pro and Premium plans. Free plan users can access Mock Mode for practice." },
        ]
      }
    ]
  },
  {
    id: "phone", label: "Phone Interviews", icon: Phone, count: 3,
    sections: [
      {
        title: "Phone & Audio-Only Interviews",
        questions: [
          { q: "Can I use Wingman AI for phone interviews?", a: "Yes. For phone interviews, keep Wingman AI open on your computer screen. When the interviewer asks a question, type it into the input box — you'll get the answer within seconds to read while you speak on the phone." },
          { q: "Does Wingman AI work on mobile?", a: "The web app is accessible from a mobile browser, but for the best experience during an active interview use a desktop or laptop so you can read answers comfortably while speaking." },
          { q: "What if I can't type fast enough during a phone call?", a: "Use shorthand or key phrases — the AI understands partial questions. For example, typing 'tell me about yourself' or 'why this company' is enough to get a complete, well-structured answer." },
        ]
      }
    ]
  },
  {
    id: "privacy", label: "Privacy & Stealth Mode", icon: Shield, count: 4,
    sections: [
      {
        title: "Visibility & Privacy",
        questions: [
          { q: "Can my interviewer see Wingman AI on my screen?", a: "Wingman AI runs in a browser tab. If you share your entire screen, the interviewer could see it if they look carefully. Best practice: share only the specific application window (Zoom, Teams, VS Code, etc.) using the 'Share a Window' option rather than sharing your full screen. This keeps the Wingman AI browser tab completely hidden." },
          { q: "Is my conversation data stored?", a: "Your session transcripts (questions and AI answers) are stored securely in your account for session history and review. We do not sell or share your data with third parties. You can delete sessions from your Session History at any time." },
          { q: "Does Wingman AI record my voice?", a: "Wingman AI uses the Web Speech Recognition API, which processes audio locally in your browser and sends transcribed text (not raw audio) to our servers for AI processing. No audio recordings are stored." },
          { q: "How do I make sure the AI panel is not visible during screen share?", a: "Use your video call app's 'Share Window' feature instead of 'Share Screen'. Select only the Zoom/Teams/Meet window. Your browser (with Wingman AI) will not be visible to the interviewer at all." },
        ]
      }
    ]
  },
  {
    id: "tips", label: "Tips & Best Practices", icon: Lightbulb, count: 5,
    sections: [
      {
        title: "Getting the Most Out of Wingman AI",
        questions: [
          { q: "What's the best way to use Wingman AI during a live interview?", a: "1. Fill out the Interview Setup with as much detail as possible — company, position, scenario, and objective. 2. Start the session 5 minutes early to test your mic. 3. During the interview, glance at the Best Answer panel while speaking naturally. 4. Don't read verbatim — use the answer as a framework and add your own personality." },
          { q: "How do I get better answers from the AI?", a: "Be specific in your setup. 'Pre-Sales Architect at ServiceNow for an enterprise deal' will get you far better answers than 'Sales at a tech company'. Use the Objective field to add context like the round type, interviewer's title, or specific focus areas." },
          { q: "Should I use Mock Mode before a real interview?", a: "Absolutely. Mock Mode puts the AI in the interviewer's seat and asks you challenging role-specific questions. Use it the day before your interview to practice articulating answers out loud and identifying weak spots." },
          { q: "How do I use the three answer options effectively?", a: "Scan the Best Answer first — it's the most complete. If it feels too long to speak in the moment, check Option 2 for a shorter version. Option 3 often takes a different angle (more technical, more story-driven) which may fit better depending on how the question was framed." },
          { q: "What should I do after each interview session?", a: "Review your Session History to read back all questions and answers. Identify which answers felt weak and practice them in Mock Mode. Over multiple sessions, you'll build a personal bank of strong answers for common questions in your role." },
        ]
      }
    ]
  },
  {
    id: "troubleshooting", label: "Troubleshooting", icon: Wrench, count: 6,
    sections: [
      {
        title: "Common Issues",
        questions: [
          { q: "The microphone isn't picking up audio — what do I do?", a: "1. Make sure you clicked 'Listen' and the green LIVE indicator is showing. 2. Check that your browser has microphone permission (look for the mic icon in the address bar). 3. Check your system's microphone settings — the right input device must be selected. 4. Try refreshing the page and clicking Listen again. 5. If still not working, use the manual text input instead." },
          { q: "The AI answer is not appearing — what's wrong?", a: "Check your internet connection first. If connected, the issue may be a temporary API delay — wait a few seconds and try again. If you see '[LLM error]' in the panel, the AI service may be temporarily unavailable; try again in a minute. Contact support if the issue persists." },
          { q: "The page is loading slowly or showing a spinner forever.", a: "Do a hard refresh (Ctrl+Shift+R on Windows, Cmd+Shift+R on Mac). If the issue continues, clear your browser cache and cookies for this site, then log in again. Make sure you're on a stable internet connection." },
          { q: "I got logged out unexpectedly.", a: "Sessions last 30 days. If you were logged out before that, it may be because you cleared browser data or used a private/incognito window. Log in again normally — your sessions and plan are saved to your account, not the browser." },
          { q: "The answer feels generic and not role-specific.", a: "This usually means the Interview Setup was not specific enough. Go back to the Dashboard, start a new session, and fill in the company and position fields precisely. Use the Objective field to add context about the interview round, the interviewer's seniority, and any specific focus areas." },
          { q: "The video feed (camera/screen) isn't working.", a: "For Camera: allow camera access in browser permissions. For Screen Share: click 'Share Screen / App', select the specific window in the system dialog, and click Share. If permission was denied, go to browser site settings and re-enable it." },
        ]
      }
    ]
  },
  {
    id: "account", label: "Account & Settings", icon: User, count: 4,
    sections: [
      {
        title: "Your Account",
        questions: [
          { q: "How do I change my password?", a: "Currently, password change is available by contacting support. Provide your registered email and we'll send reset instructions. A self-service password reset flow is on our roadmap." },
          { q: "How do I delete my account?", a: "Contact us via the Support section in the Resource Hub with your registered email address and a request to delete your account. We'll permanently delete all your data within 7 business days." },
          { q: "Can I use Wingman AI on multiple devices?", a: "Yes. Your account works on any device and browser. Log in with the same credentials and all your sessions, plan, and history are synced." },
          { q: "How do I sign out?", a: "Click 'Sign out' at the bottom of the left sidebar. You'll be immediately logged out and redirected to the landing page." },
        ]
      }
    ]
  },
  {
    id: "career", label: "Career Tools", icon: Briefcase, count: 4,
    sections: [
      {
        title: "Beyond Interviews",
        questions: [
          { q: "Does Wingman AI help with resume writing?", a: "Premium plan users get access to a Resume & Cover Letter Coach. In a session, describe your target role and your background, and the AI will generate tailored resume bullet points, summary statements, and cover letter drafts aligned to the specific company and position." },
          { q: "Can Wingman AI help me negotiate a salary offer?", a: "Yes. In any session, you can ask for help with salary negotiation — framing the counter-offer, anchoring high, addressing multiple competing offers, and total compensation framing. The AI gives you specific language you can use in the negotiation call." },
          { q: "Can Wingman AI help me prepare for a promotion discussion?", a: "Absolutely. Start a Live or Mock session and set the position to your target promotion title at your current company. Use it to rehearse articulating your impact, handling pushback, and making the business case for your promotion." },
          { q: "Is Wingman AI useful for non-technical roles?", a: "Yes. Wingman AI is built for any role — Business Analyst, Product Manager, Consultant, Pre-Sales, Marketing, Operations, and more. The AI understands domain-specific vocabulary and interview expectations for each role and tailors its answers accordingly." },
        ]
      }
    ]
  },
];

function AccordionItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10 last:border-0">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-white/[0.02] transition"
      >
        <span className="text-sm font-medium text-white">{question}</span>
        <ChevronDown className={`w-4 h-4 text-[#D3FF24] shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 pb-5">
          <p className="text-sm text-neutral-400 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  const [activeCategory, setActiveCategory] = useState("getting-started");
  const active = CATEGORIES.find((c) => c.id === activeCategory);

  return (
    <div className="px-6 py-10 max-w-6xl">
      <SEO title="FAQ — Frequently Asked Questions" description="Got questions about Project Wingman? Find answers about AI interview coaching, pricing, resume builder, cover letter generator, and more." path="/app/faq" />
      <h1 className="font-display text-5xl font-black tracking-tighter mb-2">FAQ</h1>
      <p className="text-neutral-500 mb-10">Everything you need to know about Project Wingman.</p>

      <div className="flex gap-8">
        {/* Sidebar */}
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-6 border border-white/10 overflow-hidden">
            {CATEGORIES.map((cat, i) => {
              const isActive = cat.id === activeCategory;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                    i > 0 ? "border-t border-white/10" : ""
                  } ${isActive ? "bg-[#D3FF24]/5" : "hover:bg-white/[0.02]"}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-7 h-7 flex items-center justify-center ${isActive ? "bg-[#D3FF24]/15" : "bg-white/5"}`}>
                      <cat.icon className={`w-3.5 h-3.5 ${isActive ? "text-[#D3FF24]" : "text-neutral-500"}`} strokeWidth={1.75} />
                    </div>
                    <div>
                      <p className={`text-xs font-medium ${isActive ? "text-[#D3FF24]" : "text-neutral-300"}`}>{cat.label}</p>
                      <p className="text-[10px] text-neutral-600">{cat.count} questions</p>
                    </div>
                  </div>
                  {isActive && <span className="w-1.5 h-1.5 rounded-full bg-[#D3FF24]" />}
                </button>
              );
            })}
          </div>
        </aside>

        {/* Mobile category pills */}
        <div className="lg:hidden mb-6 -mx-6 px-6">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`shrink-0 px-4 py-2 text-xs font-medium transition-colors ${
                  cat.id === activeCategory
                    ? "bg-[#D3FF24]/20 text-[#D3FF24]"
                    : "bg-white/5 text-neutral-400 hover:bg-white/10"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="mb-6">
            <h2 className="font-display text-2xl font-bold text-[#D3FF24]">{active.label}</h2>
            <p className="text-xs text-neutral-500 mt-1">{active.count} questions</p>
          </div>

          <div className="space-y-6">
            {active.sections.map((section) => (
              <div key={section.title}>
                <div className="text-[10px] uppercase tracking-widest text-neutral-600 font-semibold mb-3">{section.title}</div>
                <div className="border border-white/10 bg-[#0A0A0A]">
                  {section.questions.map((item, i) => (
                    <AccordionItem key={i} question={item.q} answer={item.a} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Support CTA */}
          <div className="mt-12 border border-white/10 bg-[#0A0A0A] p-8 text-center">
            <HelpCircle className="w-8 h-8 text-[#D3FF24] mx-auto mb-3" strokeWidth={1.5} />
            <h3 className="font-display text-lg font-semibold mb-2">Can't find what you're looking for?</h3>
            <p className="text-sm text-neutral-500 mb-6">Reach out to our team or ask the community.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="mailto:support@projectwingman.thesecretsociety.in"
                className="inline-flex items-center gap-2 bg-[#D3FF24] text-black px-6 py-2.5 text-xs uppercase tracking-widest font-semibold hover:brightness-105 transition">
                Email Support
              </a>
              <a href="https://discord.gg/t9CrSPRYV" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-white/15 px-6 py-2.5 text-xs uppercase tracking-widest font-semibold text-neutral-300 hover:text-white hover:border-white/30 transition">
                Join Discord Community
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
